/**
 * The two halves of a team's name — "Green Bay" and "Packers" — from a structured
 * target's `details`.
 *
 * ## Why this exists
 *
 * `details.team_name` used to be the bare nickname ("Packers"), so every caller
 * that wanted a full name just wrote `${market} ${team_name}`. It is now the
 * *full* team name, with the market half spelled one of two ways:
 *
 *   NFL   market='Green Bay'   team_name='GB Packers'          abbreviation='GB'
 *   NBA   market='Orlando'     team_name='ORL Magic'           abbreviation='ORL'
 *   NCAAFB market='Cincinnati' team_name='Cincinnati Bearcats'
 *
 * Measured across all 12,581 team structured targets: NFL 32/32, NBA 30/32 and
 * NCAAFB 316/316 carry a market half; NHL, MLB, WNBA, NCAAMB/WB, UFL, CFL, soccer,
 * cricket and rugby still send the bare nickname. So every join of the old shape
 * now doubles the market — "Green Bay GB Packers", "Cincinnati Cincinnati Bearcats".
 *
 * ## The rule: strip the leading token only when it is *explained*
 *
 * A token is explained when it equals `details.abbreviation`, or is the initialism
 * of `market` ("Los Angeles" → "LA"), or `market` squashed and uppercased starts
 * with it ("Orlando" → "ORL").
 *
 * Provenance, not shape. Keying on shape — "any 2-4 uppercase token" — is wrong
 * twice over. It mangles a genuine short-uppercase nickname head: "FC Copenhagen"
 * with market "Copenhagen" would become "Copenhagen". And it is unnecessary,
 * because the prefix is always derivable from a field the payload already carries.
 *
 * The rule also cannot key on `abbreviation` alone: for nine teams the prefix and
 * the abbreviation differ — NFL LAC/LAR ('LA'), NYG/NYJ ('NY'), and NBA LAL ('LA'),
 * GSW ('GS'), SAS ('SA'), NOP ('NO'), NYK ('NY').
 *
 * Validated against all 12,581 targets: no first token went unexplained, so the
 * rule strips every real prefix and nothing else. Running it over today's live NFL
 * payload reproduces `nflTeamDirectory.json` byte for byte.
 *
 * ## Keep this module dependency-free
 *
 * Like `utils/teamPageSlug`, whose header explains the 785KB edge-bundle regression
 * that one import edge caused. This is reached from both the edge middleware graph
 * and client components (`Footer`, the team page client), so it stays pure string
 * work with no imports. `utils/teams` re-exports it for callers that already import
 * that module.
 */

/** The `details` fields this module reads. Structural, so any payload shape fits. */
export interface TeamNameDetailsLike {
    market?: string | null;
    team_name?: string | null;
    abbreviation?: string | null;
}

const toTrimmed = (value: unknown): string | undefined => {
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};

/** "Los Angeles" → "LA". A one-word market yields nothing to compare. */
const toInitialism = (market: string): string | undefined => {
    const words = market.split(/\s+/).filter(Boolean);
    if (words.length < 2) {
        return undefined;
    }
    return words
        .map((word) => word[0])
        .join('')
        .toUpperCase();
};

/** "Green Bay" → "GREENBAY", so "GB" can be tested as a prefix of the market. */
const squash = (market: string): string => market.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

/**
 * Whether `token` is a market half we can account for, and can therefore drop.
 *
 * The squashed-prefix test is last and is the loosest of the three, which is why
 * it is bounded to 4 characters: without that, a nickname like "PACK" would be
 * read as a prefix of "GREENBAY"... it isn't, but a two-letter nickname head on a
 * long market could be, and a false strip costs the nickname itself.
 */
const isExplainedMarketToken = (token: string, market: string, abbreviation?: string): boolean => {
    const upperToken = token.toUpperCase();
    if (abbreviation && upperToken === abbreviation.toUpperCase()) {
        return true;
    }
    if (toInitialism(market) === upperToken) {
        return true;
    }
    return upperToken.length <= 4 && squash(market).startsWith(upperToken);
};

/**
 * The nickname alone: "Packers" from `{market: 'Green Bay', team_name: 'GB Packers'}`.
 *
 * Returns `team_name` verbatim when nothing can be accounted for, so a payload this
 * module has never seen degrades to the old behaviour rather than to a guess.
 */
export const getTeamNickname = (details: TeamNameDetailsLike | undefined): string | undefined => {
    const teamName = toTrimmed(details?.team_name);
    if (!teamName) {
        return undefined;
    }

    const market = toTrimmed(details?.market);
    if (!market) {
        return teamName;
    }

    // A target whose two fields say the same thing has no nickname to give —
    // NCAAFB sends this for schools known by their market alone.
    if (teamName.toLowerCase() === market.toLowerCase()) {
        return undefined;
    }

    // The market spelled in full ("Cincinnati Bearcats"). Checked first: it is
    // unambiguous, and it is the majority of affected targets.
    if (teamName.toLowerCase().startsWith(`${market.toLowerCase()} `)) {
        const remainder = teamName.slice(market.length).trim();
        if (remainder) {
            return remainder;
        }
    }

    // The market as a short token ("GB Packers", "ORL Magic").
    const firstSpace = teamName.search(/\s/);
    if (firstSpace > 0) {
        const head = teamName.slice(0, firstSpace);
        const remainder = teamName.slice(firstSpace).trim();
        if (remainder && isExplainedMarketToken(head, market, toTrimmed(details?.abbreviation))) {
            return remainder;
        }
    }

    return teamName;
};

/**
 * Market and nickname, for callers that label the two halves separately.
 */
export const getTeamNameParts = (
    details: TeamNameDetailsLike | undefined,
): { market: string | undefined; nickname: string | undefined } => ({
    market: toTrimmed(details?.market),
    nickname: getTeamNickname(details),
});

/**
 * The full team name: "Green Bay Packers".
 *
 * `undefined` when either half is missing, so callers keep their existing
 * "both halves or nothing" fallbacks rather than emitting half a name — the
 * failure mode that turns a team-page link into a 404.
 */
export const getFullTeamName = (details: TeamNameDetailsLike | undefined): string | undefined => {
    const { market, nickname } = getTeamNameParts(details);
    if (!market || !nickname) {
        return undefined;
    }
    return `${market} ${nickname}`;
};
